import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import CustomPopUp from "../global/CustomPopup";
import CloseIcon from "../../assets/svg/CloseIcon";
import {
  useActivateMfa,
  useEnrollMfa,
} from "../../services/utils/custom hooks/Mfa/useMfaFactors";
import toastMessages from "../../services/utils/helpers/toastMessage";
import useProfile from "../../services/utils/custom hooks/useProfile";
import MfaFactor, { MfaEnrollMethod } from "../../services/models/mfaFactor.model";
import {
  getMfaDaysRemaining,
  getTotpQrHref,
  getTotpSharedSecret,
} from "../../services/utils/helpers/mfaHelpers";

type SetupStep = "select" | "email_otp" | "totp_activate" | "success";

interface MfaSetupPopupProps {
  onClose: () => void;
  onEnrolled?: () => void;
  /** Soft launch: show skip / remind later */
  allowSkip?: boolean;
  /** Hard enforce: hide close & skip */
  required?: boolean;
  /** Adding the method that is not enrolled yet. Hides the other choice. */
  lockedMethod?: MfaEnrollMethod;
}

const ctaClassName =
  "flex w-full items-center justify-center gap-2 rounded-lg bg-[#117A74] px-4 py-3 font-sans text-sm font-semibold text-white transition-colors hover:bg-[#0E6964] disabled:cursor-not-allowed disabled:opacity-60";

function daysBannerText(daysRemaining: number): string {
  if (daysRemaining <= 0) return "Complete setup to keep your account protected.";
  if (daysRemaining === 1) return "You have 1 day to complete setup.";
  return `You have ${daysRemaining} days to complete setup.`;
}

function ShieldIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3.2l6.2 2.4v4.7c0 3.8-2.5 6.5-6.2 7.9-3.7-1.4-6.2-4.1-6.2-7.9V5.6L12 3.2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 12.1l1.8 1.8 3.8-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="7" y="2.5" width="10" height="19" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M11 18.5h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12 6.2l1.1 1.1-.4 1.6 1.6-.4 1.1 1.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4.5 7.2l7.5 5.4 7.5-5.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MailBadgeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5.5" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 7.2l8 5.5 8-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17.5" cy="16.5" r="3.2" fill="#E7F6F4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M16.2 16.5l.9.9 1.8-1.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8.5V12l2.4 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="10.5" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8.5 10.5V8a3.5 3.5 0 017 0v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg className="block h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RadioMark({ selected }: Readonly<{ selected: boolean }>) {
  return (
    <span
      className={`relative h-5 w-5 shrink-0 self-center rounded-full border-2 box-border ${
        selected ? "border-[#117A74] bg-[#117A74]" : "border-[#C5CDD6] bg-white"
      }`}
      aria-hidden
    >
      {selected && (
        <span className="absolute left-1/2 top-1/2 h-1/2 w-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
      )}
    </span>
  );
}

function MethodCard({
  selected,
  title,
  description,
  badge,
  icon,
  onSelect,
}: Readonly<{
  selected: boolean;
  title: string;
  description: string;
  badge?: string;
  icon: JSX.Element;
  onSelect: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors sm:px-4 ${
        selected
          ? "border-[#8ED0CB] bg-[#F4FBFA]"
          : "border-roundedInputBorder bg-white hover:border-borderGray"
      }`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          selected ? "bg-[#E7F6F4] text-[#117A74]" : "bg-sideNavGray text-[#5C6B82]"
        }`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-sans text-sm font-semibold text-royalBlue">{title}</span>
          {badge && (
            <span className="rounded-full bg-[#E7F6F4] px-2 py-0.5 font-sans text-[10px] font-semibold text-[#117A74]">
              {badge}
            </span>
          )}
        </span>
        <span className="mt-0.5 block font-sans text-xs leading-snug text-darkGray">
          {description}
        </span>
      </span>
      <RadioMark selected={selected} />
    </button>
  );
}

function OtpBoxes({
  value,
  onChange,
  disabled,
  className = "",
}: Readonly<{
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
}>) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  function commit(next: string) {
    onChange(next.replace(/\D/g, "").slice(0, 6));
  }

  function handleChange(index: number, raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    if (digits.length > 1) {
      const merged = (value.slice(0, index) + digits).slice(0, 6);
      commit(merged);
      inputs.current[Math.min(merged.length, 5)]?.focus();
      return;
    }
    const next = (value.slice(0, index) + digits + value.slice(index + 1)).slice(0, 6);
    commit(next);
    if (index < 5) inputs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowRight" && index < 5) {
      inputs.current[index + 1]?.focus();
      return;
    }
    if (event.key !== "Backspace") return;
    event.preventDefault();
    if (value[index]) {
      commit(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    if (index > 0) {
      commit(value.slice(0, index - 1) + value.slice(index));
      inputs.current[index - 1]?.focus();
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const text = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    commit(text);
    inputs.current[Math.min(text.length, 5)]?.focus();
  }

  return (
    <div className={`flex w-full gap-1.5 sm:gap-2 ${className}`}>
      {Array.from({ length: 6 }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          value={value[index] ?? ""}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={index === 0 ? 6 : 1}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of 6`}
          className="h-11 min-w-0 flex-1 rounded-lg border border-roundedInputBorder bg-white text-center font-sans text-lg font-semibold text-royalBlue outline-none focus:border-[#117A74] focus:ring-1 focus:ring-[#117A74] disabled:opacity-60"
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

function Eyebrow({ text }: Readonly<{ text: string }>) {
  return (
    <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.16em] text-[#117A74]">
      {text}
    </p>
  );
}

function IconBadge({ children }: Readonly<{ children: JSX.Element }>) {
  return (
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-[#E7F6F4] text-[#117A74] [&>svg]:!h-7 [&>svg]:!w-7">
      {children}
    </div>
  );
}

function SecurityFooter() {
  return (
    <div className="mt-5 flex items-center justify-center gap-1.5 border-t border-roundedInputBorder pt-3 font-sans text-xs text-darkGray">
      <LockIcon />
      An extra step to help protect your account.
    </div>
  );
}

function LaterButton({
  disabled,
  onClick,
}: Readonly<{
  disabled?: boolean;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      className="w-full py-1 text-center font-sans text-sm font-medium text-[#3D4C66] hover:text-royalBlue disabled:opacity-50"
      disabled={disabled}
      onClick={onClick}
    >
      Set up later
    </button>
  );
}

function MfaSetupPopup({
  onClose,
  onEnrolled,
  allowSkip = true,
  required = false,
  lockedMethod,
}: Readonly<MfaSetupPopupProps>): JSX.Element {
  const { data: profile } = useProfile();
  const enrollMutation = useEnrollMfa();
  const activateMutation = useActivateMfa();

  const [step, setStep] = useState<SetupStep>("select");
  const [method, setMethod] = useState<MfaEnrollMethod>(
    lockedMethod ?? "okta_verify"
  );
  const [factorId, setFactorId] = useState<string | null>(null);
  const [pendingFactor, setPendingFactor] = useState<MfaFactor | null>(null);
  const [maskedEmail, setMaskedEmail] = useState(profile?.user?.email ?? "");
  const [passCode, setPassCode] = useState("");
  const [showManualKey, setShowManualKey] = useState(false);

  const daysRemaining = getMfaDaysRemaining();
  const isBusy = enrollMutation.isPending || activateMutation.isPending;
  const canDismiss = allowSkip && !required;
  const showClose = canDismiss || step === "success";

  function handleStartEnrollment() {
    enrollMutation.mutate(method, {
      onSuccess: (factor) => {
        setFactorId(factor.id);
        setPendingFactor(factor);
        setPassCode("");
        setShowManualKey(false);
        if (method === "email") {
          setMaskedEmail(factor.profile?.email || profile?.user?.email || "");
          setStep("email_otp");
        } else {
          setStep("totp_activate");
        }
      },
    });
  }

  function handleResendEmail() {
    enrollMutation.mutate("email", {
      onSuccess: (factor) => {
        setFactorId(factor.id);
        setPendingFactor(factor);
        setMaskedEmail(factor.profile?.email || profile?.user?.email || "");
        setPassCode("");
      },
    });
  }

  async function handleVerify() {
    if (!factorId) {
      toastMessages.error("Start setup again to continue.");
      setStep("select");
      return;
    }
    if (!passCode || passCode.length < 6) {
      toastMessages.error("Enter the 6-digit verification code.");
      return;
    }

    try {
      await activateMutation.mutateAsync({ factorId, passCode });
      setStep("success");
      onEnrolled?.();
    } catch {
      // Toast is shown by the mutation
    }
  }

  const qrHref = getTotpQrHref(pendingFactor ?? undefined);
  const sharedSecret = getTotpSharedSecret(pendingFactor ?? undefined);
  const continueLabel =
    method === "email" ? "Continue with email" : "Continue with authenticator app";

  return (
    <CustomPopUp
      popUpTitle=""
      hidePopUp={showClose ? onClose : () => undefined}
      hideHeader
      cardClassName={`!rounded-2xl shadow-custom ${
        step === "totp_activate"
          ? "w-[min(92vw,42rem)]"
          : step === "email_otp"
            ? "w-[min(92vw,32rem)]"
            : "w-[min(92vw,36rem)]"
      }`}
    >
      <div className="relative w-full font-sans">
        <div className="mb-3 flex min-h-[1.5rem] items-center">
          {step !== "select" && step !== "success" && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 font-sans text-sm font-medium leading-none text-royalBlue hover:text-[#117A74] disabled:opacity-50"
              disabled={isBusy}
              onClick={() => {
                setPassCode("");
                setShowManualKey(false);
                setStep("select");
              }}
            >
              <ArrowLeftIcon />
              Change method
            </button>
          )}
          {showClose && (
            <button
              type="button"
              aria-label="Close"
              className="absolute right-0 top-0 text-darkGray hover:text-royalBlue"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {step === "select" && (
          <div className="flex flex-col">
            <div className="px-1 text-center">
              <IconBadge>
                <ShieldIcon />
              </IconBadge>
              <div className="mt-3">
                <Eyebrow text="Account security" />
              </div>
              <h2 className="mt-2 font-sans text-2xl font-semibold leading-tight tracking-tight text-royalBlue">
                Add an extra layer
                <br />
                of security
              </h2>
              <p className="mt-2 font-sans text-sm text-darkGray">
                Choose how you&apos;ll verify it&apos;s you when signing in.
              </p>
            </div>

            {canDismiss && !lockedMethod && (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#EAF4FB] px-3 py-2.5 font-sans text-sm text-[#245C86]">
                <span className="text-[#2F6F9E]">
                  <ClockIcon />
                </span>
                {daysBannerText(daysRemaining)}
              </div>
            )}
            {required && (
              <p className="mt-4 text-center font-sans text-xs font-medium text-requiredRed">
                Required to continue using the portal
              </p>
            )}

            <div className="mt-4 flex flex-col gap-2.5">
              {(!lockedMethod || lockedMethod === "okta_verify") && (
                <MethodCard
                  selected={method === "okta_verify"}
                  title="Authenticator app"
                  badge="Recommended"
                  description="Get a code from an app on your phone."
                  icon={<PhoneIcon />}
                  onSelect={() => setMethod("okta_verify")}
                />
              )}
              {(!lockedMethod || lockedMethod === "email") && (
                <MethodCard
                  selected={method === "email"}
                  title="Email"
                  description="Receive a code in your email inbox."
                  icon={<MailIcon />}
                  onSelect={() => setMethod("email")}
                />
              )}
            </div>

            <button
              type="button"
              className={`${ctaClassName} mt-4`}
              disabled={isBusy}
              onClick={handleStartEnrollment}
            >
              {enrollMutation.isPending ? "Please wait" : continueLabel}
              {!enrollMutation.isPending && <ArrowRightIcon />}
            </button>
            {canDismiss && <div className="mt-3"><LaterButton disabled={isBusy} onClick={onClose} /></div>}
            <SecurityFooter />
          </div>
        )}

        {step === "email_otp" && (
          <div className="flex flex-col">
            <div className="px-2 text-center">
              <IconBadge>
                <MailBadgeIcon />
              </IconBadge>
              <div className="mt-3">
                <Eyebrow text="Email verification" />
              </div>
              <h2 className="mt-2 font-sans text-2xl font-semibold tracking-tight text-royalBlue">
                Check your inbox
              </h2>
              <p className="mt-2 font-sans text-sm text-darkGray">
                Enter the 6-digit code we sent to
              </p>
              <p className="font-sans text-sm font-semibold text-royalBlue">
                {maskedEmail || "your email"}
              </p>
            </div>

            <div className="mt-5 w-full">
              <p className="mb-2 text-center font-sans text-sm font-medium text-royalBlue">
                Verification code
              </p>
              <OtpBoxes
                value={passCode}
                onChange={setPassCode}
                disabled={isBusy}
                className="w-full"
              />
              <p className="mt-2 font-sans text-xs text-darkGray">You can paste the entire code.</p>
              <p className="mt-3 font-sans text-sm text-darkGray">
                Didn&apos;t get a code?{" "}
                <button
                  type="button"
                  className="font-semibold text-[#117A74] disabled:opacity-50"
                  disabled={isBusy}
                  onClick={handleResendEmail}
                >
                  Resend code
                </button>
              </p>
            </div>

            <button
              type="button"
              className={`${ctaClassName} mt-5`}
              disabled={isBusy || passCode.length !== 6}
              onClick={() => {
                void handleVerify();
              }}
            >
              {activateMutation.isPending ? "Please wait" : "Verify and enable"}
            </button>
            {canDismiss && <div className="mt-3"><LaterButton disabled={isBusy} onClick={onClose} /></div>}
            <SecurityFooter />
          </div>
        )}

        {step === "totp_activate" && (
          <div className="flex flex-col">
            <div className="px-2 text-center">
              <IconBadge>
                <PhoneIcon />
              </IconBadge>
              <div className="mt-3">
                <Eyebrow text="Authenticator setup" />
              </div>
              <h2 className="mt-2 font-sans text-2xl font-semibold tracking-tight text-royalBlue">
                Connect your authenticator app
              </h2>
              <p className="mt-2 font-sans text-sm text-darkGray">
                Scan the QR code, then enter the code from your app.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="flex items-start gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#117A74] font-sans text-xs font-semibold text-white">
                    1
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold leading-5 text-royalBlue">Scan the QR code</p>
                    <p className="mt-1 font-sans text-xs leading-5 text-darkGray">
                      In <span className="font-semibold text-textSecondaryGray">Okta Verify</span> or another authenticator app, add an account.
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-col items-center">
                  {qrHref ? (
                    <img
                      src={qrHref}
                      alt="Authenticator QR code"
                      className="h-36 w-36 rounded-md border border-roundedInputBorder bg-white p-2"
                    />
                  ) : (
                    <div className="flex h-36 w-36 items-center justify-center rounded-md border border-roundedInputBorder bg-sideNavGray px-3 text-center">
                      <p className="font-sans text-xs text-darkGray">
                        QR code unavailable. Enter the key manually.
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    className="mt-2 font-sans text-xs font-medium text-[#117A74]"
                    onClick={() => setShowManualKey((open) => !open)}
                  >
                    Can&apos;t scan? Enter key manually
                  </button>
                  {showManualKey && (
                    <code className="mt-2 max-w-full break-all text-center font-sans text-xs font-semibold text-royalBlue">
                      {sharedSecret || "Setup key unavailable. Go back and start again."}
                    </code>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-start gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#117A74] font-sans text-xs font-semibold text-white">
                    2
                  </span>
                  <div>
                    <p className="font-sans text-sm font-semibold leading-5 text-royalBlue">Enter your code</p>
                    <p className="mt-1 pt-px font-sans text-xs leading-5 text-darkGray">
                      Enter the 6-digit code shown in your authenticator app.
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="mb-2 text-left font-sans text-sm font-medium text-royalBlue">
                    Verification code
                  </p>
                  <OtpBoxes
                    value={passCode}
                    onChange={setPassCode}
                    disabled={isBusy}
                    className="max-w-none"
                  />
                  <p className="mt-2 text-left font-sans text-xs text-darkGray">You can paste the entire code.</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`${ctaClassName} mt-5`}
              disabled={isBusy || passCode.length !== 6}
              onClick={() => {
                void handleVerify();
              }}
            >
              {activateMutation.isPending ? "Please wait" : "Verify and enable"}
            </button>
            {canDismiss && <div className="mt-3"><LaterButton disabled={isBusy} onClick={onClose} /></div>}
            <SecurityFooter />
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center text-center">
            <IconBadge>
              <ShieldIcon />
            </IconBadge>
            <h2 className="mt-3 font-sans text-2xl font-semibold text-royalBlue">
              Two-step verification is on
            </h2>
            <p className="mt-2 font-sans text-sm text-darkGray">
              You will be asked for a code when signing in from a new device.
            </p>
            <button type="button" className={`${ctaClassName} mt-5`} onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </CustomPopUp>
  );
}

export default MfaSetupPopup;
